/**
 * Where a new player came from, as a short label for the stats (net/stats.ts SITE): never an address, only a name
 * from the list below (a site nobody listed is "other").
 *   from:<tag>   a link posted with ?from=<tag> (grandprem.io/?from=simracing): exact, even inside apps
 *   in-discord   the game opened inside Discord
 *   friend-link  a friend's lap link (?lap=…)
 *   reddit, google, x, …   the site the browser says the visitor came from (only its name)
 *   direct       nothing said: typed, a bookmark, or an app that hides where its links lead from
 */

/** Sites by the end of their address (the longest match wins), and Android apps by their package. */
const SITES: Record<string, string> = {
  'reddit.com': 'reddit',
  'redd.it': 'reddit',
  'com.reddit.frontpage': 'reddit',
  'news.ycombinator.com': 'hackernews',
  'discord.com': 'discord',
  'discordapp.com': 'discord',
  'com.discord': 'discord',
  't.co': 'x',
  'x.com': 'x',
  'twitter.com': 'x',
  'com.twitter.android': 'x',
  'bsky.app': 'bluesky',
  'facebook.com': 'facebook',
  'fb.com': 'facebook',
  'messenger.com': 'facebook',
  'com.facebook.katana': 'facebook',
  'instagram.com': 'instagram',
  'com.instagram.android': 'instagram',
  'threads.net': 'threads',
  'tiktok.com': 'tiktok',
  'youtube.com': 'youtube',
  'youtu.be': 'youtube',
  'linkedin.com': 'linkedin',
  'lnkd.in': 'linkedin',
  'whatsapp.com': 'whatsapp',
  'wa.me': 'whatsapp',
  'com.whatsapp': 'whatsapp',
  't.me': 'telegram',
  'telegram.org': 'telegram',
  'org.telegram.messenger': 'telegram',
  'github.com': 'github',
  'threejs.org': 'threejs',
  'itch.io': 'itch',
  'producthunt.com': 'producthunt',
  'crazygames.com': 'crazygames',
  'poki.com': 'poki',
  'newgrounds.com': 'newgrounds',
  'bing.com': 'bing',
  'duckduckgo.com': 'duckduckgo',
  'search.brave.com': 'brave',
  'ecosia.org': 'ecosia',
  'search.yahoo.com': 'yahoo',
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'claude.ai': 'claude',
  'com.google.android.googlequicksearchbox': 'google',
  'com.google.android.gm': 'gmail',
  'mail.google.com': 'gmail',
};

/** The game's own addresses: coming from one of them says nothing about where the player came from. */
const OWN = ['grandprem.io', 'workers.dev', 'discordsays.com', 'localhost', '127.0.0.1'];

/** A ?from= tag as kept: lower case, letters, digits and dashes, at most 24 (empty: no tag). */
export function fromTag(raw: string | null): string {
  return (raw ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24)
    .replace(/-+$/, '');
}

/** The site a referrer names ('' when it names none, or one of the game's own). */
export function siteOf(referrer: string): string {
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return '';
  }
  if (!host || OWN.some((o) => host === o || host.endsWith(`.${o}`))) return '';
  // Google is google.com, google.it, google.co.uk…
  if (/(^|\.)google\.[a-z.]+$/.test(host) && !host.startsWith('mail.')) return 'google';
  let best = '';
  for (const k of Object.keys(SITES)) if ((host === k || host.endsWith(`.${k}`)) && k.length > best.length) best = k;
  return best ? SITES[best] : 'other';
}

/** Where this visit came from (see the top of this file). */
export function arrivalSite(search: string, referrer: string, inDiscord: boolean): string {
  const q = new URLSearchParams(search);
  const tag = fromTag(q.get('from'));
  if (tag) return `from:${tag}`;
  if (inDiscord) return 'in-discord';
  if (q.get('lap')) return 'friend-link';
  return siteOf(referrer) || 'direct';
}

/** The address without its ?from= (once read): passed on, it would count someone else's arrival under that post. */
export function withoutFrom(href: string): string | null {
  const u = new URL(href);
  if (!u.searchParams.has('from')) return null;
  u.searchParams.delete('from');
  return `${u.pathname}${u.search}${u.hash}`;
}
